import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, RotateCcw, Eye, ArrowLeft, FileText } from 'lucide-react';
import Modal, { ModalFormGrid, ModalFormColumn, FormGroup } from '../components/common/Modal';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';
import serviceCategoryService from '../services/serviceCategoryService';
import '../styles/FormSections.css';

const ServiceCategoryListPage = () => {
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();
  
  // Estados
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    isActive: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    requiredDocuments: []
  });

  // Cargar categorías
  const loadCategories = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: itemsPerPage,
        search: searchTerm,
        isActive: filters.isActive
      };
      
      const data = await serviceCategoryService.getCategories(params);
      setCategories(data.categories || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalItems(data.pagination?.totalItems || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error cargando categorías:', error);
      showNotification('Error al cargar las categorías', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, itemsPerPage, showNotification]);

  // Abrir modal de creación
  const openCreateModal = () => {
    setFormData({
      name: '',
      description: '',
      isActive: 'true',
      requiredDocuments: []
    });
    setIsEditing(false);
    setEditingCategory(null);
    setShowModal(true);
  };

  // Abrir modal de edición
  const openEditModal = (category) => {
    setFormData({
      name: category.name,
      description: category.description || '',
      isActive: String(category.isActive),
      requiredDocuments: category.requiredDocuments || []
    });
    setIsEditing(true);
    setEditingCategory(category);
    setShowModal(true);
  };

  // Cerrar modal
  const closeModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      isActive: 'true',
      requiredDocuments: []
    });
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Manejar cambio de documentos requeridos
  const handleDocumentChange = (docType, isChecked) => {
    setFormData(prev => {
      const newDocs = isChecked 
        ? [...prev.requiredDocuments, docType]
        : prev.requiredDocuments.filter(d => d !== docType);
      
      return {
        ...prev,
        requiredDocuments: newDocs
      };
    });
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let data;
      if (isEditing) {
        data = await serviceCategoryService.updateCategory(editingCategory._id, formData);
      } else {
        data = await serviceCategoryService.createCategory(formData);
      }
      
      showNotification(data.message, 'success');
      closeModal();
      loadCategories();
    } catch (error) {
      console.error('Error guardando categoría:', error);
      showNotification(error.message || 'Error al guardar la categoría', 'error');
    }
  };

  // Eliminar categoría
  const handleDelete = async (categoryId, categoryName) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar la categoría "${categoryName}"?\n\nEsta acción no se puede deshacer.`)) {
      try {
        const data = await serviceCategoryService.deleteCategory(categoryId);
        showNotification(data.message, 'success');
        loadCategories();
      } catch (error) {
        console.error('Error eliminando categoría:', error);
        showNotification(error.message || 'Error al eliminar la categoría', 'error');
      }
    }
  };

  // Restaurar categoría
  const handleRestore = async (categoryId, categoryName) => {
    try {
      const data = await serviceCategoryService.restoreCategory(categoryId);
      showNotification(data.message, 'success');
      loadCategories();
    } catch (error) {
      console.error('Error restaurando categoría:', error);
      showNotification(error.message || 'Error al restaurar la categoría', 'error');
    }
  };

  // Manejar filtros
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ isActive: '' });
    setCurrentPage(1);
  };

  // Navegación de páginas
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadCategories(newPage);
    }
  };

  // Cambiar página anterior
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  // Cambiar página siguiente
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Manejar cambio de registros por página
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    loadCategories(1);
  };

  // Las categorías ya vienen filtradas del servidor
  const filteredCategories = categories;

  // Cargar datos iniciales
  useEffect(() => {
    loadCategories(1);
  }, [loadCategories]);

  // Recargar cuando cambien los filtros o búsqueda
  useEffect(() => {
    if (currentPage === 1) {
      loadCategories(1);
    } else {
      setCurrentPage(1);
      loadCategories(1);
    }
  }, [searchTerm, filters]);

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/services')}
            className="btn btn-outline"
            title="Volver a Servicios"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Categorías de Servicios</h1>
            <p className="text-gray-600">
              Gestiona las categorías para organizar tus servicios
            </p>
          </div>
        </div>
        <button
          onClick={openCreateModal}
          className="btn btn-primary"
        >
          <Plus size={16} />
          Nueva Categoría
        </button>
      </div>

      {/* Contenedor de filtros */}
      <div className="card mb-6 filters-container">
        <div className="card-content">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Campo de búsqueda */}
            <div className="flex-1 min-w-64">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nombre de la categoría..."
                className="form-input"
              />
            </div>

            {/* Filtro por estado */}
            <div className="w-48">
              <label className="form-label">Estado</label>
              <select
                value={filters.isActive}
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                className="form-input"
              >
                <option value="">Todos los estados</option>
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>

            {/* Botón limpiar */}
            <div className="w-48">
              <label className="form-label">&nbsp;</label>
              <button
                onClick={clearFilters}
                className="btn btn-outline w-full"
              >
                🗑️ Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Selector de registros por página */}
      <div className="records-per-page">
        <label htmlFor="itemsPerPage">Registros por página:</label>
        <select
          id="itemsPerPage"
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      {/* Lista de categorías */}
      <div className="table-container">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="loading-spinner"></div>
            <p className="mt-4 text-gray-600">Cargando categorías...</p>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">📂</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || filters.isActive ? 'No se encontraron resultados' : 'No hay categorías'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filters.isActive ? 
                'No hay categorías que coincidan con los filtros aplicados' : 
                'Comienza agregando tu primera categoría'
              }
            </p>
            {!searchTerm && !filters.isActive && (
              <button
                onClick={openCreateModal}
                className="btn btn-primary"
              >
                <Plus size={16} />
                Agregar Categoría
              </button>
            )}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Documentos Requeridos</th>
                <th>Estado</th>
                <th>Creado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map((category) => (
                <tr key={category._id}>
                  <td>
                    <div className="font-medium text-gray-900">{category.name}</div>
                  </td>
                  <td>
                    <div className="text-sm text-gray-600">
                      {category.description || 'Sin descripción'}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {category.requiredDocuments?.length > 0 ? (
                        category.requiredDocuments.map(doc => (
                          <span key={doc} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                            {doc === 'COTIZACION' ? 'Cotización' : 
                             doc === 'DIMENSIONAMIENTO' ? 'Dimensionamiento' : 
                             doc === 'DOC_TECNICO' ? 'Doc. Técnico' : doc}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400">Ninguno</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${category.isActive ? 'badge-success' : 'badge-secondary'}`}>
                      {category.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="text-sm text-gray-700">
                    {new Date(category.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(category)}
                        className="btn btn-sm"
                        style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: 'white' }}
                        title="Editar"
                      >
                        <Edit size={16} />
                      </button>
                      {category.isActive ? (
                        <button
                          onClick={() => handleDelete(category._id, category.name)}
                          className="btn btn-danger btn-sm"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRestore(category._id, category.name)}
                          className="btn btn-success btn-sm"
                          title="Restaurar"
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de creación/edición */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={isEditing ? 'Editar Categoría' : 'Crear Categoría'}
        onSubmit={handleSubmit}
        submitText={isEditing ? 'Actualizar' : 'Crear'}
      >
        {/* Información Básica */}
        <div className="form-section">
          <h4 className="form-section-title">Información Básica</h4>
          <ModalFormGrid>
            <ModalFormColumn>
              <FormGroup label="Nombre de la Categoría" required htmlFor="name">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Ej. Desarrollo Web"
                  required
                />
              </FormGroup>
            </ModalFormColumn>
          </ModalFormGrid>

          <FormGroup label="Descripción" htmlFor="description">
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-input form-textarea"
              placeholder="Descripción de la categoría"
              rows="3"
            />
          </FormGroup>
        </div>

        {/* Requisitos Documentales */}
        <div className="form-section">
          <h4 className="form-section-title flex items-center gap-2">
            <FileText size={18} className="text-blue-600" />
            Requisitos Documentales
          </h4>
          <p className="text-sm text-gray-500 mb-3">
            Selecciona los documentos que serán obligatorios para confirmar ventas de servicios en esta categoría.
          </p>
          <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="req-cotizacion"
                checked={formData.requiredDocuments.includes('COTIZACION')}
                onChange={(e) => handleDocumentChange('COTIZACION', e.target.checked)}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <label htmlFor="req-cotizacion" className="font-medium text-gray-700 cursor-pointer">
                Cotización Final
              </label>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="req-dimensionamiento"
                checked={formData.requiredDocuments.includes('DIMENSIONAMIENTO')}
                onChange={(e) => handleDocumentChange('DIMENSIONAMIENTO', e.target.checked)}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <label htmlFor="req-dimensionamiento" className="font-medium text-gray-700 cursor-pointer">
                Documento de Dimensionamiento
              </label>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="req-tecnico"
                checked={formData.requiredDocuments.includes('DOC_TECNICO')}
                onChange={(e) => handleDocumentChange('DOC_TECNICO', e.target.checked)}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <label htmlFor="req-tecnico" className="font-medium text-gray-700 cursor-pointer">
                Documento Técnico
              </label>
            </div>
          </div>
        </div>

        {/* Estado */}
        <div className="form-section">
          <h4 className="form-section-title">Estado</h4>
          <CustomSelect
            label="Estado"
            value={formData.isActive}
            onChange={(value) => handleInputChange({ target: { name: 'isActive', value } })}
            options={[
              { value: 'true', label: 'Activo' },
              { value: 'false', label: 'Inactivo' }
            ]}
            placeholder="Seleccionar estado"
          />
        </div>
      </Modal>

      {/* Notificación */}
      <NotificationSystem 
        notification={notification} 
        onClose={hideNotification} 
      />
    </div>
  );
};

export default ServiceCategoryListPage;